import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import FilmCard from '@/components/base/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import MultiSelect from '@/components/base/MultiSelect';
import { categories } from '@/mocks/categories';
import { films } from '@/mocks/films';
import { filterFilms, formatDuration, getCategoryName, sortFilms, yearOptions } from '@/utils/content';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';

  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [yearFilter, setYearFilter] = useState<string[]>([]);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta({
    title: keyword ? `搜索「${keyword}」- 成人影视` : '搜索 - 成人影视',
    description: keyword
      ? `在成人影视资料库中搜索「${keyword}」相关的内容介绍与影视条目，支持按类型与年份进一步筛选。`
      : '输入关键词检索成人影视内容与资讯，支持按类型、年份筛选，快速定位感兴趣的影视作品。',
    keywords: '成人影视搜索,影视检索,影视资讯,内容查找',
  });

  const categoryOptions = categories.map((category) => category.name);

  const results = useMemo(() => {
    const types = typeFilter.map((name) => categories.find((item) => item.name === name)?.id ?? '');
    const matched = filterFilms(films, {
      keyword,
      types: types.filter(Boolean),
      years: yearFilter,
    });
    return sortFilms(matched, keyword ? 'latest' : 'popular');
  }, [keyword, typeFilter, yearFilter]);

  const hotFilms = sortFilms(films, 'popular').slice(0, 5);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setTypeFilter([]);
    setYearFilter([]);
  };

  const suggestions = ['夜色', '独立制作', '韩国', '纪实', '修复版'];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="border-b border-background-300/70 bg-background-100">
          <div className="page-shell py-8 md:py-12">
            <p className="eyebrow mb-2">Search</p>
            <h1 className="text-2xl font-semibold text-foreground-950 md:text-[32px]">搜索影视内容</h1>
            <p className="mt-2 max-w-2xl text-[13px] leading-7 text-foreground-600">
              支持片名、导演、演员、地区与标签的模糊匹配。也可以只输入一个关键词，再用下方筛选条件缩小范围。
            </p>

            <form onSubmit={submitSearch} className="mt-6 flex w-full max-w-3xl flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  type="search"
                  name="keyword"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="输入片名、导演、演员或标签关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-[13px] placeholder:text-foreground-500 focus:border-primary-500/70 focus:ring-1 focus:ring-primary-500/30"
                />
              </div>
              <button
                type="submit"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-line" />
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-label text-[10px] tracking-wider text-foreground-500">试试</span>
              {suggestions.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => setSearchParams({ q: word })}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-500/60 hover:text-primary-400"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="page-shell pt-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-300/70 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <MultiSelect label="类型" options={categoryOptions} selected={typeFilter} onChange={setTypeFilter} />
              <MultiSelect label="年份" options={yearOptions} selected={yearFilter} onChange={setYearFilter} />
              {(typeFilter.length > 0 || yearFilter.length > 0) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-[12px] text-foreground-500 transition-colors hover:text-primary-400"
                >
                  <i className="ri-close-line" />
                  清除筛选
                </button>
              )}
            </div>
            <p className="shrink-0 text-[12px] text-foreground-600">
              {keyword ? (
                <>
                  关键词「<span className="text-primary-400">{keyword}</span>」共找到{' '}
                  <span className="text-foreground-950">{results.length}</span> 条结果
                </>
              ) : (
                <>
                  未输入关键词时，展示 <span className="text-foreground-950">{results.length}</span> 条热门内容
                </>
              )}
            </p>
          </div>

          {results.length > 0 ? (
            <div className="mt-6 space-y-3">
              {results.map((film, index) => (
                <Link
                  key={film.id}
                  to={`/detail/${film.id}`}
                  className="group flex cursor-pointer gap-4 rounded-lg border border-background-300/70 bg-background-100 p-3 transition-colors hover:border-primary-500/40 hover:bg-background-200/50 animate-rise sm:p-4"
                  style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
                >
                  <div className="h-[132px] w-[88px] shrink-0 overflow-hidden rounded-md border border-background-300/60 bg-background-200 sm:h-[150px] sm:w-[100px]">
                    <img
                      src={film.cover}
                      alt={`${film.title} 搜索结果封面`}
                      title={`${film.title} · 成人影视条目`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-secondary-500/15 px-1.5 py-0.5 font-label text-[10px] tracking-wider text-secondary-700">
                        {getCategoryName(film.category)}
                      </span>
                      <span className="text-[10px] text-foreground-500">{film.subcategory}</span>
                    </div>
                    <h2 className="mt-1.5 truncate text-[15px] font-medium text-foreground-950 transition-colors group-hover:text-primary-400">
                      {film.title}
                    </h2>
                    <p className="mt-1 line-clamp-2 text-[12px] leading-6 text-foreground-600">{film.summary}</p>
                    <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-foreground-500">
                      <span className="flex items-center gap-1 text-primary-400">
                        <i className="ri-star-fill text-[9px]" />
                        {film.rating.toFixed(1)}
                      </span>
                      <span>{film.year}</span>
                      <span>{film.region}</span>
                      <span>{formatDuration(film.duration)}</span>
                      <span className="hidden sm:inline">导演 {film.director}</span>
                    </div>
                  </div>
                  <span className="hidden items-center pr-2 text-foreground-500 transition-colors group-hover:text-primary-400 sm:flex">
                    <i className="ri-arrow-right-s-line text-xl" />
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                icon="ri-search-eye-line"
                title={keyword ? '没有找到匹配的内容' : '暂无符合条件的条目'}
                description={
                  keyword
                    ? `没有与「${keyword}」匹配的条目。可以尝试更简短的关键词，或清除筛选条件后重新查找。`
                    : '当前筛选条件下没有条目，可以调整筛选条件或浏览下方热门内容。'
                }
                primaryAction={{ label: '清除筛选条件', onClick: clearFilters }}
                secondaryAction={{ label: '返回首页', to: '/' }}
              />
            </div>
          )}
        </section>

        <section className="page-shell pt-14">
          <div className="mb-6">
            <p className="eyebrow mb-2">Popular picks</p>
            <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">热门内容推荐</h2>
            <p className="mt-2 text-[12px] leading-6 text-foreground-500">
              按条目热度排序，作为搜索无结果时的内容补充。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-5">
            {hotFilms.map((film, index) => (
              <FilmCard key={film.id} film={film} index={index} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}